import React from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { CheckCircle, Package, Mail, Clock, AlertCircle } from 'lucide-react';
import { GoogleReviewPrompt } from '@/components/GoogleReviewPrompt';

export default function OrderConfirmation() {
  const location = useLocation();
  const navigate = useNavigate();
  const { order, isWholesale, quantity } = location.state || {};

  if (!order) {
    return (
      <div className="min-h-screen py-24 md:py-32 flex flex-col items-center justify-center">
        <p className="text-xl text-muted-foreground mb-4">No order found.</p>
        <button onClick={() => navigate('/products')} className="text-primary hover:underline">Browse Products</button>
      </div>
    );
  }

  return (
    <div className="min-h-screen py-24 md:py-32">
      <div className="max-w-3xl mx-auto px-6 md:px-12">
        <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} className="text-center mb-12">
          <div className={`w-20 h-20 ${isWholesale ? 'bg-amber-100' : 'bg-green-100'} rounded-full flex items-center justify-center mx-auto mb-6`}>
            {isWholesale ? <AlertCircle className="w-10 h-10 text-amber-600" /> : <CheckCircle className="w-10 h-10 text-green-600" />}
          </div>
          <h1 className="text-4xl md:text-5xl font-bold mb-4 font-heading" data-testid="confirmation-title">
            {isWholesale ? 'Wholesale Request Received!' : 'Order Request Received!'}
          </h1>
          <p className="text-lg text-muted-foreground">
            {isWholesale
              ? `Thank you for your interest in IIY BCM-95®. We have received your wholesale request for ${quantity} bottles. Our sales team is reviewing your request to provide you with our best custom wholesale pricing. We will be in touch shortly via email with your specialized rate and a secure link to complete your purchase.`
              : 'Thank you for your order request with IIY Phytoventra Ltd. We are processing your request and will send a final confirmation shortly.'}
          </p>
        </motion.div>

        {!isWholesale && order.total && (
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }} className="bg-white rounded-3xl p-8 shadow-soft mb-8">
            <h2 className="text-xl font-bold mb-4 font-heading">Order Details</h2>
            <div className="space-y-3 text-sm">
              <div className="flex justify-between py-2 border-b border-border"><span className="text-muted-foreground">Order ID</span><span className="font-mono text-xs">{order.order_id || order.id}</span></div>
              <div className="flex justify-between py-2 border-b border-border"><span className="text-muted-foreground">Quantity</span><span>{order.quantity} bottles</span></div>
              {order.price_per_bottle && <div className="flex justify-between py-2 border-b border-border"><span className="text-muted-foreground">Price per Bottle</span><span>£{order.price_per_bottle?.toFixed(2)}</span></div>}
              {order.is_vat_business && order.vat_amount ? (
                <>
                  <div className="flex justify-between py-2 border-b border-border"><span className="text-muted-foreground">Net (ex-VAT)</span><span>£{order.subtotal?.toFixed(2)}</span></div>
                  <div className="flex justify-between py-2 border-b border-border"><span className="text-muted-foreground">VAT (20%)</span><span>£{order.vat_amount?.toFixed(2)}</span></div>
                </>
              ) : null}
              {order.total && <div className="flex justify-between py-3 font-bold text-lg"><span>Total</span><span>£{order.total?.toFixed(2)}</span></div>}
              {!order.is_vat_business && order.total && (
                <p className="text-[11px] text-muted-foreground leading-snug">All-inclusive of UK VAT (20%) and Royal Mail 48 delivery.</p>
              )}
            </div>
          </motion.div>
        )}

        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 }} className="grid md:grid-cols-3 gap-4 mb-8">
          <div className="bg-blue-50 rounded-2xl p-5 text-center"><Mail className="w-6 h-6 text-blue-600 mx-auto mb-2" /><p className="text-sm font-medium">{isWholesale ? 'Quote Coming' : 'Confirmation Email'}</p><p className="text-xs text-muted-foreground">{isWholesale ? 'Custom pricing within 24hrs' : 'Check your inbox'}</p></div>
          <div className="bg-amber-50 rounded-2xl p-5 text-center"><Clock className="w-6 h-6 text-amber-600 mx-auto mb-2" /><p className="text-sm font-medium">Processing</p><p className="text-xs text-muted-foreground">{isWholesale ? 'Team reviewing your request' : 'Preparing your order'}</p></div>
          <div className="bg-green-50 rounded-2xl p-5 text-center"><Package className="w-6 h-6 text-green-600 mx-auto mb-2" /><p className="text-sm font-medium">Dispatch</p><p className="text-xs text-muted-foreground">{isWholesale ? 'After payment confirmed' : 'Shipped once confirmed'}</p></div>
        </motion.div>

        {isWholesale && (
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.4 }} className="bg-amber-50 border border-amber-200 rounded-2xl p-6 mb-8">
            <p className="text-sm text-amber-800">If you have any immediate questions, please contact us at <a href="mailto:sales@iiypv.com" className="font-bold underline">sales@iiypv.com</a> or <a href="mailto:support@iiypv.com" className="font-bold underline">support@iiypv.com</a></p>
          </motion.div>
        )}

        {!isWholesale && <GoogleReviewPrompt />}

        <div className="text-center">
          <button onClick={() => navigate('/')} className="bg-primary hover:bg-primary/90 text-white font-semibold py-3 px-8 rounded-xl transition-all duration-300" data-testid="back-home-button">Back to Home</button>
        </div>
      </div>
    </div>
  );
}
